import Papa from 'papaparse';

export interface ParseCsvOutput<T> {
  results: Papa.ParseResult<T>;
  file: File;
}

export const readCsv = async <T = any>(inputFile: File, config?: Papa.ParseConfig<T>) => {
  const promise = new Promise<ParseCsvOutput<T>>((resolve, reject) => {
    Papa.parse(inputFile, {
      ...config,
      skipEmptyLines: 'greedy',
      complete: (results, file) => resolve({ results, file }),
      error: (error) => reject(error),
    });
  });

  return promise;
};

export interface ParseCsvResult<T> {
  data: T[];
  errors: { row: number; message: string }[];
}

/**
 * Reads a CSV file and parses each row into a typed object using the header row as keys (trimmed, used as-is).
 * CSV headers must exactly match the keys of T.
 * Optionally validates each row.
 */
export const parseCsv = async <T extends Record<string, any>>(
  inputFile: File,
  validator?: (row: T, index: number) => void | string
): Promise<ParseCsvResult<T>> => {
  const { results } = await readCsv(inputFile);
  const rawRows = results.data as string[][];

  if (rawRows.length < 2) {
    return { data: [], errors: [] };
  }

  const headers = rawRows[0].map((h) => h.trim());

  const data: T[] = [];
  const errors: { row: number; message: string }[] = [];

  for (let i = 1; i < rawRows.length; i++) {
    const rawRow = rawRows[i];
    if (!rawRow || rawRow.every((cell) => !cell.trim())) continue;

    const row = {} as Record<string, string>;
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = rawRow[j]?.trim() ?? '';
    }

    if (validator) {
      const result = validator(row as T, i);

      if (typeof result === 'string') {
        errors.push({ row: i, message: result });
        continue;
      }
    }

    data.push(row as T);
  }

  return { data, errors };
};
